/**
 * Settings — admin-only page for app-wide configuration.
 * Currently covers:
 *   • AI Search daily rate limit (DataForSEO budget guard)
 *   • Competitor suggestion blocklist (hides noisy aggregator domains from
 *     the Suggested competitors rail on every client, team-wide).
 * Non-admins are redirected to their account page.
 */
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { RerunRanksCard } from "../components/settings/RerunRanksCard";
import { Sliders, Save, Sparkles, Info, Shield, Plus, X, Loader2, Newspaper, RotateCcw } from "lucide-react";

const inputCls =
  "w-full rounded-xl border border-[#e2e8f0] bg-white px-3.5 py-2.5 text-sm text-[#1a202c] focus:border-[#4fd1c5] focus:ring-2 focus:ring-[#4fd1c5]/15 outline-none transition-all font-mono-tight";
const labelCls = "text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold";

function normDomain(s) {
  return (s || "").trim().replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/.*$/, "").toLowerCase();
}

export default function Settings() {
  const { isAdmin } = useAuth();
  const [limit, setLimit] = useState("");
  const [usage, setUsage] = useState(null);
  const [tz, setTz] = useState("Europe/London");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Competitor blocklist state
  const [userBlocklist, setUserBlocklist] = useState([]);
  const [builtinBlocklist, setBuiltinBlocklist] = useState([]);
  const [blocklistInput, setBlocklistInput] = useState("");
  const [savingBlocklist, setSavingBlocklist] = useState(false);
  const [showBuiltin, setShowBuiltin] = useState(false);

  // Industry news feeds state
  const [newsFeeds, setNewsFeeds] = useState([]);
  const [defaultFeeds, setDefaultFeeds] = useState([]);
  const [newsName, setNewsName] = useState("");
  const [newsUrl, setNewsUrl] = useState("");
  const [savingNews, setSavingNews] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const [{ data: s }, { data: u }] = await Promise.all([
          api.get("/settings"),
          api.get("/tools/ai-search/usage"),
        ]);
        setLimit(String(s.ai_search_daily_limit));
        setTz(s.tz || "Europe/London");
        setUsage(u);
        setUserBlocklist(s.competitor_suggestion_blocklist || []);
        setBuiltinBlocklist(s.competitor_suggestion_blocklist_builtin || []);
        setNewsFeeds(s.industry_news_feeds || []);
        setDefaultFeeds(s.industry_news_feeds_default || []);
      } catch (e) {
        toast.error("Couldn't load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/account" replace />;

  const save = async (e) => {
    e.preventDefault();
    const n = parseInt(limit, 10);
    if (!Number.isFinite(n) || n < 1 || n > 1000) {
      toast.error("Limit must be a number between 1 and 1000");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/settings", { ai_search_daily_limit: n });
      const { data: u } = await api.get("/tools/ai-search/usage");
      setUsage(u);
      toast.success("Settings updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update settings");
    } finally {
      setSaving(false);
    }
  };

  const persistBlocklist = async (next) => {
    setSavingBlocklist(true);
    try {
      const { data } = await api.patch("/settings", { competitor_suggestion_blocklist: next });
      setUserBlocklist(data.competitor_suggestion_blocklist || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update blocklist");
    } finally {
      setSavingBlocklist(false);
    }
  };

  const addToBlocklist = async (e) => {
    e.preventDefault();
    const dom = normDomain(blocklistInput);
    if (!dom) return;
    if (builtinBlocklist.includes(dom)) {
      toast.info(`${dom} is already in the built-in list.`);
      setBlocklistInput("");
      return;
    }
    if (userBlocklist.includes(dom)) {
      toast.info(`${dom} is already blocked.`);
      setBlocklistInput("");
      return;
    }
    const next = [...userBlocklist, dom].sort();
    setBlocklistInput("");
    await persistBlocklist(next);
    toast.success(`Blocked ${dom} across all clients.`);
  };

  const removeFromBlocklist = async (dom) => {
    const next = userBlocklist.filter((d) => d !== dom);
    await persistBlocklist(next);
    toast.success(`Unblocked ${dom}.`);
  };

  const persistNewsFeeds = async (next) => {
    setSavingNews(true);
    try {
      const { data } = await api.patch("/settings", { industry_news_feeds: next });
      setNewsFeeds(data.industry_news_feeds || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update feed list");
    } finally {
      setSavingNews(false);
    }
  };

  const addNewsFeed = async (e) => {
    e.preventDefault();
    const url = newsUrl.trim();
    const name = newsName.trim() || url;
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      toast.error("Feed URL must start with http:// or https://");
      return;
    }
    if (newsFeeds.some((f) => f.url === url)) {
      toast.info("That feed is already in the list.");
      return;
    }
    setNewsName("");
    setNewsUrl("");
    await persistNewsFeeds([...newsFeeds, { name, url }]);
    toast.success(`Added ${name}. The dashboard news card will refresh on next load.`);
  };

  const removeNewsFeed = async (url) => {
    await persistNewsFeeds(newsFeeds.filter((f) => f.url !== url));
    toast.success("Feed removed.");
  };

  const resetNewsFeeds = async () => {
    await persistNewsFeeds(defaultFeeds);
    toast.success("Reset to default sources.");
  };

  const resetLabel = usage?.reset_at
    ? new Date(usage.reset_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "midnight";

  return (
    <div className="space-y-5 max-w-2xl" data-testid="settings-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Settings</div>
        <h1 className="font-display text-3xl mt-0.5 text-[#1a202c] flex items-center gap-2.5">
          <Sliders className="h-6 w-6 text-[#4fd1c5]" strokeWidth={2} /> App settings
        </h1>
        <p className="text-xs text-[#718096] mt-1">Global configuration — applies to every user.</p>
      </div>

      {/* AI Search daily limit */}
      <form onSubmit={save} className="card-soft p-5 space-y-5">
        <div className="flex items-center gap-2.5">
          <div className="h-7 w-7 rounded-lg bg-[#ecfeff] border border-[#67e8f9] flex items-center justify-center">
            <Sparkles className="h-3.5 w-3.5 text-[#0e7490]" strokeWidth={2.4} />
          </div>
          <h2 className="font-display text-base text-[#1a202c]">AI Search daily limit</h2>
        </div>

        <div>
          <label className={labelCls} htmlFor="ai-search-limit-input">Max prompts per day (global)</label>
          <div className="flex items-center gap-3 mt-1.5">
            <input
              id="ai-search-limit-input"
              data-testid="settings-ai-search-limit"
              type="number"
              min={1}
              max={1000}
              required
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className={`${inputCls} max-w-[140px] text-center`}
              disabled={loading}
            />
            <div className="text-[11px] text-[#718096]">
              Applies to all users combined. Resets at midnight ({tz}).
            </div>
          </div>
        </div>

        {usage && (
          <div
            className="rounded-xl border border-[#e2e8f0] bg-[#f7fafc] px-4 py-3 flex items-center gap-3"
            data-testid="settings-usage-preview"
          >
            <Info className="h-3.5 w-3.5 text-[#718096]" />
            <div className="text-[12px] text-[#4a5568]">
              Today so far:{" "}
              <span className="font-semibold text-[#1a202c] font-mono-tight">
                {usage.used} / {usage.limit}
              </span>{" "}
              <span className="text-[#a0aec0]">
                · {usage.remaining} remaining · next reset {resetLabel}
              </span>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || loading}
            data-testid="settings-save-btn"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>

      <RerunRanksCard />

      {/* Competitor suggestion blocklist */}
      <div className="card-soft p-5 space-y-4" data-testid="settings-competitor-blocklist">
        <div className="flex items-center gap-2.5">
          <div className="h-7 w-7 rounded-lg bg-[#fff5f5] border border-[#feb2b2] flex items-center justify-center">
            <Shield className="h-3.5 w-3.5 text-[#c53030]" strokeWidth={2.4} />
          </div>
          <h2 className="font-display text-base text-[#1a202c]">Competitor suggestion blocklist</h2>
        </div>
        <p className="text-xs text-[#718096]">
          Domains in this list are filtered out of the <strong>Suggested competitors</strong> rail on every client,
          so you don't have to dismiss the same aggregators over and over. Dismissing a domain with "Block across
          all clients" from a suggestion chip also lands here.
        </p>

        <form onSubmit={addToBlocklist} className="flex gap-2 items-center">
          <input
            value={blocklistInput}
            onChange={(e) => setBlocklistInput(e.target.value)}
            placeholder="e.g. aggregator.co.uk"
            data-testid="settings-blocklist-input"
            disabled={savingBlocklist}
            className={inputCls}
          />
          <button
            type="submit"
            disabled={savingBlocklist || !blocklistInput.trim()}
            data-testid="settings-blocklist-add"
            className="btn-primary inline-flex items-center gap-2"
          >
            {savingBlocklist ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Block
          </button>
        </form>

        {userBlocklist.length === 0 ? (
          <div className="text-[11px] text-[#a0aec0] italic">
            No custom domains blocked yet. Built-in aggregators (wikipedia, reddit, pinterest, amazon, etc.) are
            always filtered.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="settings-blocklist-chips">
            {userBlocklist.map((dom) => (
              <div
                key={dom}
                data-testid={`settings-blocklist-chip-${dom}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#fed7d7] bg-[#fff5f5] px-2.5 py-1 text-xs text-[#9b2c2c] font-mono-tight"
              >
                {dom}
                <button
                  onClick={() => removeFromBlocklist(dom)}
                  disabled={savingBlocklist}
                  data-testid={`settings-blocklist-remove-${dom}`}
                  title="Unblock"
                  className="h-4 w-4 rounded-full bg-[#feb2b2]/50 hover:bg-[#feb2b2] text-[#742a2a] inline-flex items-center justify-center transition-colors"
                >
                  <X className="h-2.5 w-2.5" strokeWidth={2.8} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="pt-1">
          <button
            type="button"
            onClick={() => setShowBuiltin((v) => !v)}
            data-testid="settings-blocklist-toggle-builtin"
            className="text-[11px] text-[#718096] hover:text-[#1a202c] font-mono-tight"
          >
            {showBuiltin ? "▼" : "▸"} Built-in blocklist ({builtinBlocklist.length} domains)
          </button>
          {showBuiltin && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="settings-blocklist-builtin-chips">
              {builtinBlocklist.map((dom) => (
                <span
                  key={dom}
                  className="inline-flex items-center rounded-md border border-[#edf2f7] bg-[#f7fafc] px-2 py-0.5 text-[10px] text-[#718096] font-mono-tight"
                >
                  {dom}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Industry news RSS feeds */}
      <div className="card-soft p-5 space-y-4" data-testid="settings-news-feeds">
        <div className="flex items-center gap-2.5">
          <div className="h-7 w-7 rounded-lg bg-[#faf5ff] border border-[#e9d8fd] flex items-center justify-center">
            <Newspaper className="h-3.5 w-3.5 text-[#6b46c1]" strokeWidth={2.4} />
          </div>
          <h2 className="font-display text-base text-[#1a202c]">Industry news sources</h2>
        </div>
        <p className="text-xs text-[#718096]">
          RSS feeds powering the <strong>Industry news</strong> card on the dashboard. Results are
          cached for 1 hour across the whole app, so any change here appears on the next refresh. We
          round-robin headlines so every working source gets fair shelf space on the first page.
        </p>

        <form onSubmit={addNewsFeed} className="grid grid-cols-1 sm:grid-cols-[1fr_1.5fr_auto] gap-2">
          <input
            value={newsName}
            onChange={(e) => setNewsName(e.target.value)}
            placeholder="Source name (optional)"
            data-testid="settings-news-name-input"
            disabled={savingNews}
            className={inputCls}
          />
          <input
            value={newsUrl}
            onChange={(e) => setNewsUrl(e.target.value)}
            placeholder="https://example.com/feed.xml"
            data-testid="settings-news-url-input"
            disabled={savingNews}
            className={inputCls}
          />
          <button
            type="submit"
            disabled={savingNews || !newsUrl.trim()}
            data-testid="settings-news-add"
            className="btn-primary inline-flex items-center gap-2"
          >
            {savingNews ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Add feed
          </button>
        </form>

        {newsFeeds.length === 0 ? (
          <div className="text-[11px] text-[#a0aec0] italic">No feeds configured — the dashboard will show the default six.</div>
        ) : (
          <div className="rounded-xl border border-[#edf2f7] divide-y divide-[#edf2f7] overflow-hidden" data-testid="settings-news-feeds-list">
            {newsFeeds.map((f) => (
              <div
                key={f.url}
                data-testid={`settings-news-feed-${f.url}`}
                className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-[#f7fafc] transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-[#1a202c]">{f.name}</div>
                  <div className="text-[10px] text-[#a0aec0] font-mono-tight truncate" title={f.url}>{f.url}</div>
                </div>
                <button
                  onClick={() => removeNewsFeed(f.url)}
                  disabled={savingNews}
                  data-testid={`settings-news-remove-${f.url}`}
                  title="Remove feed"
                  className="h-5 w-5 rounded-full bg-[#f7fafc] hover:bg-[#fed7d7] hover:text-[#c53030] text-[#718096] inline-flex items-center justify-center transition-colors flex-none"
                >
                  <X className="h-3 w-3" strokeWidth={2.8} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={resetNewsFeeds}
            disabled={savingNews}
            data-testid="settings-news-reset"
            className="btn-ghost inline-flex items-center gap-2 text-[11px]"
          >
            <RotateCcw className="h-3 w-3" /> Reset to defaults
          </button>
          <div className="text-[10px] text-[#a0aec0] font-mono-tight">
            Defaults: {defaultFeeds.map((f) => f.name).join(" · ")}
          </div>
        </div>
      </div>
    </div>
  );
}
